import React, { useState, useMemo } from "react";
import { Search } from "lucide-react";
import type { Bank, CountryCode } from "../types";

interface BankPickerProps {
  onBankSelect: (bank: Bank) => void;
  onCsvImport?: () => void;
}

// Minimal in-component bank registry so search returns real results.
// In production this becomes useBanks(country) hook that fetches the full
// global list; results render with real BankLogo per row.
const BANK_REGISTRY: Bank[] = [
  { code: "rbc", name: "RBC Royal Bank", shortName: "RBC", country: "ca", isPopular: true },
  { code: "td", name: "TD Canada Trust", shortName: "TD", country: "ca", isPopular: true },
  { code: "scotia", name: "Scotiabank", shortName: "SB", country: "ca", isPopular: true },
  { code: "bmo", name: "BMO Bank of Montreal", shortName: "BMO", country: "ca", isPopular: true },
  { code: "cibc", name: "CIBC", shortName: "CIBC", country: "ca", isPopular: true },
  { code: "nbc", name: "National Bank of Canada", shortName: "NBC", country: "ca", isPopular: false },
  { code: "tangerine", name: "Tangerine", shortName: "TG", country: "ca", isPopular: false },
  { code: "simplii", name: "Simplii Financial", shortName: "SM", country: "ca", isPopular: false },
  { code: "desjardins", name: "Desjardins", shortName: "DJ", country: "ca", isPopular: false },
  { code: "atb", name: "ATB Financial", shortName: "ATB", country: "ca", isPopular: false },
  { code: "vancity", name: "Vancity", shortName: "VC", country: "ca", isPopular: false },
  { code: "meridian", name: "Meridian Credit Union", shortName: "MC", country: "ca", isPopular: false },
  { code: "eqbank", name: "EQ Bank", shortName: "EQ", country: "ca", isPopular: false },
  { code: "hsbc_ca", name: "HSBC Canada", shortName: "HS", country: "ca", isPopular: false },
];

const USER_COUNTRY: CountryCode = "ca"; // From user profile, hardcoded until auth lands.

export function BankPicker({ onBankSelect, onCsvImport }: BankPickerProps) {
  const [query, setQuery] = useState("");
  const [focused, setFocused] = useState(false);

  const results = useMemo(() => {
    if (!query.trim()) return [];
    const q = query.trim().toLowerCase();
    return BANK_REGISTRY.filter(
      (b) => b.country === USER_COUNTRY && b.name.toLowerCase().includes(q),
    ).slice(0, 8);
  }, [query]);

  const showDropdown = focused && query.trim().length > 0;

  return (
    <div className="relative">
      <p className="text-[10.5px] font-bold uppercase tracking-wider text-ink3 mb-2.5">
        Find your bank
      </p>
      <div className="relative max-w-xl">
        <div className="flex items-center gap-3 bg-white border border-line rounded-full px-5 py-2.5 focus-within:border-ink">
          <Search size={16} strokeWidth={2} className="text-ink4" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => setTimeout(() => setFocused(false), 150)}
            placeholder="Start typing your bank's name..."
            className="flex-1 bg-transparent outline-none text-[13.5px] placeholder:text-ink4"
          />
        </div>

        {showDropdown && (
          <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-line rounded-xl shadow-card max-h-80 overflow-y-auto z-20">
            {results.length === 0 ? (
              <div className="px-4 py-3 text-[12.5px] text-ink3">
                No banks match "{query}". Try a different spelling{onCsvImport ? ", or import a CSV below" : ""}.
              </div>
            ) : (
              results.map((bank) => (
                <button
                  key={bank.code}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => onBankSelect(bank)}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-surface-paper text-left"
                >
                  <span className="w-8 h-8 rounded-md bg-sage text-ink2 font-extrabold text-[11px] inline-flex items-center justify-center flex-shrink-0">
                    {bank.shortName}
                  </span>
                  <span className="text-[13px] font-semibold text-ink">{bank.name}</span>
                </button>
              ))
            )}
          </div>
        )}
      </div>

      <p className="text-[11.5px] text-ink3 mt-3 mb-6 max-w-xl">
        Supports thousands of banks and credit unions globally. Real logos appear as you type.
      </p>

      {onCsvImport && (
        <div className="pt-3.5 border-t border-lineSoft text-[12.5px] text-ink2">
          <strong className="text-ink font-semibold">Can't find your bank?</strong>{" "}
          <button
            type="button"
            onClick={onCsvImport}
            className="text-teal font-semibold border-b border-transparent hover:border-teal"
          >
            Import a CSV instead
          </button>
        </div>
      )}
    </div>
  );
}